import React, { Fragment, useRef, useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useSelector, useDispatch } from 'react-redux';
import Label from 'src/components/_utility/Label';
import Radio from 'components/_form/Radio/Radio';
import RangeSlider from 'components/_form/RangeSlider/RangeSlider';
import { setFilterChoice, setFilterRange, setActiveFilter, resetFilter } from 'src/redux/bikes/actions';
import BackIcon from 'src/assets/icons/chevron-left.svg';
import { filtersSelector } from 'src/redux/app/selectors';
import { getBikeFilter, isFilterSet } from 'src/redux/bikes/selectors';
import { getImageBaseUrl } from 'helpers/request';
import ResetIcon from 'src/assets/icons/reset.svg';
import ArrowDown from 'src/assets/icons/arrow-down.svg';
import { isIOS } from 'helpers/utilities';
import useDataLayer from 'src/hooks/useDataLayer';
import {
  ClickCatch,
  Dialog,
  DialogHeader,
  Description,
  RangeDescription,
  BackButton,
  OptionsWrap,
  FilterTitle,
  DialogBody,
  DescriptionWrap,
  FilterIcon,
  ResetFilterButton,
  ScrollIndicator,
} from './FilterDialog.styled';

const Range = ({ filterIndex, bikeFilter }) => {
  const dispatch = useDispatch();
  const pushToDataLayer = useDataLayer();

  const { from: bikeFilterFrom, to: bikeFilterTo, min: floor, max: ceiling } = useSelector(getBikeFilter(filterIndex));

  const fromValue = bikeFilter.values.find(value => parseInt(value.value, 10) === bikeFilterFrom);
  const toValue = bikeFilter.values.find(value => parseInt(value.value, 10) === bikeFilterTo);
  const fromText = fromValue.text;
  const toText = toValue.text;
  const fromIndex = bikeFilter.values.indexOf(fromValue);
  const toIndex = bikeFilter.values.indexOf(toValue);

  const canIncrease = bikeFilterTo < ceiling;
  const canDecrease = bikeFilterFrom > floor;

  const handleIncrease = () => {
    dispatch(
      setFilterRange({
        filter: filterIndex,
        to: parseInt(bikeFilter.values[toIndex + 1].value, 10),
        from: bikeFilterFrom,
      })
    );
  };

  const handleDecrease = () => {
    dispatch(
      setFilterRange({
        filter: filterIndex,
        from: parseInt(bikeFilter.values[fromIndex - 1].value, 10),
        to: bikeFilterTo,
      })
    );
  };

  const marks = bikeFilter.values.reduce((prevMarks, filter) => {
    prevMarks[parseInt(filter.value, 10)] = parseInt(filter.value, 10);

    return prevMarks;
  }, {});

  return (
    <Fragment>
      <RangeDescription>
        <Label name="fromLabel" /> <span>{fromText}</span> <Label name="toLabel" factory={val => val.toLowerCase()} />{' '}
        <span>{toText}</span>
      </RangeDescription>
      <RangeSlider
        min={floor}
        max={ceiling}
        value={[bikeFilterFrom, bikeFilterTo]}
        onChange={([from, to]) => {
          pushToDataLayer(`configuratorFilter${filterIndex + 1}`, {
            configuratorFilter: `${from}-${to}`,
          });
          dispatch(setFilterRange({ filter: filterIndex, from, to }));
        }}
        step={null}
        marks={marks}
        onDecreaseQuantity={handleDecrease}
        onIncreaseQuantity={handleIncrease}
        canDecrease={canDecrease}
        canIncrease={canIncrease}
      />
    </Fragment>
  );
};

const Choice = ({ filterIndex, values = [] }) => {
  const dispatch = useDispatch();
  const pushToDataLayer = useDataLayer();
  const { choice } = useSelector(getBikeFilter(filterIndex));

  return (
    <Fragment>
      <Radio
        id={`${filterIndex}-show-all`}
        label="showAllLabel"
        data-testid="showAll"
        checked={!choice}
        onChange={() => {
          pushToDataLayer(`configuratorFilter${filterIndex + 1}`, {
            configuratorFilter: 'Show all',
          });
          dispatch(setFilterChoice({ filter: filterIndex, choice: null }));
        }}
        isWhite
      />
      {values.map(option => (
        <Radio
          id={option.value}
          key={option.value}
          data-testid={option.value}
          label={option.text}
          checked={choice === option.value}
          onChange={() => {
            pushToDataLayer(`configuratorFilter${filterIndex + 1}`, {
              configuratorFilter: option.text,
            });
            dispatch(setFilterChoice({ filter: filterIndex, choice: option.value }));
          }}
          isWhite
        />
      ))}
    </Fragment>
  );
};

const FilterDialog = ({ filterIndex, title, description, type, values, icon }) => {
  const [scrollIndicatorVisible, setScrollIndicatorVisible] = useState(false);
  const dispatch = useDispatch();
  const filters = useSelector(filtersSelector);
  const filterSet = useSelector(isFilterSet(filterIndex));
  const bikeFilter = filters[filterIndex];
  const dialogBodyRef = useRef();

  const componentSwitch = {
    slider: Range,
    options: Choice,
  };
  const FilterBody = componentSwitch[type];

  useEffect(() => {
    const dialogBodyEl = dialogBodyRef.current;

    setTimeout(() => {
      dialogBodyEl.scrollTop = isIOS() ? -(dialogBodyEl.scrollHeight - dialogBodyEl.getBoundingClientRect().height) : 0;
    });

    const handleDialogBodyScroll = () => {
      const getScrollTop = el => {
        if (isIOS()) {
          return el.scrollTop + el.scrollHeight - el.getBoundingClientRect().height;
        }

        return el.scrollTop;
      };

      setScrollIndicatorVisible(
        getScrollTop(dialogBodyEl) + dialogBodyEl.getBoundingClientRect().height < dialogBodyEl.scrollHeight
      );
    };

    dialogBodyEl.addEventListener('scroll', handleDialogBodyScroll);
  }, []);

  return (
    <>
      <ClickCatch data-testid="clickCatch" onClick={() => dispatch(setActiveFilter(null))} />
      <Dialog>
        <DialogHeader>
          <BackButton data-testid="backButton" onClick={() => dispatch(setActiveFilter(null))}>
            <BackIcon />
          </BackButton>
          {title}
        </DialogHeader>
        <DialogBody ref={dialogBodyRef}>
          <OptionsWrap>
            <FilterTitle>{title}</FilterTitle>
            {FilterBody ? <FilterBody filterIndex={filterIndex} bikeFilter={bikeFilter} values={values} /> : null}
          </OptionsWrap>
          <DescriptionWrap>
            <FilterIcon>
              <SVG src={getImageBaseUrl() + icon} alt={title} />
              {filterSet ? (
                <ResetFilterButton data-testid="resetFilter" onClick={() => dispatch(resetFilter(filterIndex))}>
                  <ResetIcon></ResetIcon>
                  <Label name="resetFilterLabel" />
                </ResetFilterButton>
              ) : null}
            </FilterIcon>
            {description ? <Description>{description}</Description> : null}
          </DescriptionWrap>
        </DialogBody>
        {scrollIndicatorVisible ? (
          <ScrollIndicator>
            <ArrowDown />
          </ScrollIndicator>
        ) : null}
      </Dialog>
    </>
  );
};

export default FilterDialog;
